import React from 'react';
import {
  Label,
  Input,
  Textarea,
  Select,
  SelectItem,
  RadioGroup,
  Switch,
  Toggle,
  DateInput,
  TextInput,
  NumberInput,
  ColorInput
} from '../ui';
import { MultiSelect } from '../ui/MultiSelect';
import Autocomplete from '../ui/Autocomplete';
import FormFileUpload from '../ui/FormFileUpload';
import FormFileUpload_2 from '../ui/FormFileUpload_2';
import { QuestionCircleIcon, LockIcon } from '../ui/Icons';
import { cn } from '../../lib/utils';

/**
 * Field rendering utilities
 * Extracted from GenericFormsPage for better maintainability
 */

// Enhanced required field indicator with configurable style
export const RequiredIndicator = ({ isRequired, config }) => {
  if (!isRequired) return null;
  
  // Configuration: Change this to switch styles
  const requiredFieldStyle = config.ui?.requiredFieldStyle || 'badge';
  
  const styles = {
    // Design Option 1: Clean badge (default)
    badge: (
      <span className="inline-flex items-center ml-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 border border-red-200">
          Required
        </span>
      </span>
    ),
    
    // Design Option 2: Modern pill badge
    pill: (
      <span className="inline-flex items-center px-2 py-0.5 ml-2 text-xs font-medium bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-full shadow-sm">
        Required
      </span>
    ),
    
    // Design Option 3: Icon with text
    icon: (
      <span className="inline-flex items-center gap-1 ml-1 text-red-600">
        <QuestionCircleIcon className="w-3 h-3" />
        <span className="text-xs font-medium">Required</span>
      </span>
    ),
    
    // Design Option 4: Subtle underline
    underline: (
      <span className="inline-flex items-center ml-1">
        <span className="text-xs font-medium text-red-600 border-b border-red-300 border-dotted">
          Required
        </span>
      </span>
    ),
    
    // Design Option 5: Minimal with hover indicator
    minimal: (
      <span className="inline-flex items-center ml-2">
        <span className="relative group">
          <span className="text-xs font-medium text-red-600 cursor-help border-b border-red-300 border-dotted">
            Required
          </span>
          <span className="absolute left-0 -top-8 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
            Required field
          </span>
        </span>
      </span>
    )
  };
  
  return styles[requiredFieldStyle] || styles.badge;
};

// Main field renderer
export const renderField = (field, {
  value,
  formData,
  setFormData,
  formErrors,
  setFormErrors,
  validateField,
  labels,
  config,
  setFilesToDelete,
  theme,
  confirmTaskDelete
}) => {
  const parseTaskListValue = (rawValue) => {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((item) => ({
          task: String(item?.task ?? item?.Task ?? ''),
          isDone: Boolean(item?.isDone ?? item?.IsDone),
        }));
    }

    if (typeof rawValue === 'string' && rawValue.trim()) {
      try {
        const parsed = JSON.parse(rawValue);
        if (!Array.isArray(parsed)) return [];
        return parsed
          .map((item) => ({
            task: String(item?.task ?? item?.Task ?? ''),
            isDone: Boolean(item?.isDone ?? item?.IsDone),
          }));
      } catch (error) {
        return [];
      }
    }

    return [];
  };

  const toTaskListString = (tasks) => {
    const normalized = (Array.isArray(tasks) ? tasks : [])
      .map((item) => ({
        Task: String(item?.task ?? item?.Task ?? ''),
        IsDone: Boolean(item?.isDone ?? item?.IsDone),
      }));
    return JSON.stringify(normalized);
  };

  // Required: always, or conditionally when dependsOn is met
  const isRequired = (() => {
    if (field.validation?.required === true || field.required === true) {
      return field.required !== 'conditional';
    }
    if (field.required === 'conditional' && field.dependsOn) {
      const { field: dependsOnField, value: dependsOnValue } = field.dependsOn;
      if (typeof dependsOnValue === 'function') {
        return !!dependsOnValue(formData, config?.lookupData || {});
      }
      return formData?.[dependsOnField] === dependsOnValue;
    }
    return false;
  })();
  const hasError = formErrors[field.name];
  const fieldErrorClass = hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '';

  if (field.customComponent) {
    const CustomComponent = field.customComponent;
    return (
      <div key={field.name} className="mb-0">
        <Label>
          {field.label}
          <RequiredIndicator isRequired={isRequired} config={config} />
        </Label>
        <CustomComponent
          value={value}
          timezone={formData?.timezone}
          onChange={async (nextValue) => {
            // Guard against no-op update loops from custom components.
            if (nextValue === formData?.[field.name]) {
              return;
            }
            setFormData((prev) => ({ ...prev, [field.name]: nextValue }));
            const error = await validateField(field.name, nextValue);
            setFormErrors((prev) => {
              const nextError = error || '';
              if ((prev?.[field.name] || '') === nextError) {
                return prev;
              }
              return { ...prev, [field.name]: nextError };
            });
          }}
        />
        {field.helperText ? (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{field.helperText}</div>
        ) : null}
        {hasError && (
          <div className="mt-1 text-sm text-red-600 dark:text-red-400">
            {formErrors[field.name]}
          </div>
        )}
      </div>
    );
  }
  
  switch (field.type) {
    case 'hidden':
      return <input key={field.name} type="hidden" name={field.name} value={value} />;
    
    case 'label':
      // Handle nested object values (e.g., currentRevision.version)
      let labelValue = value;
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        labelValue = value.version || value.name || value.title || value.userDisplayName || '';
      }
      
      // Convert to string if not already
      if (labelValue !== null && labelValue !== undefined) {
        labelValue = String(labelValue);
      }
      
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-sm font-medium">
            {labelValue || <span className="text-gray-400 dark:text-gray-500 italic">Not set</span>}
          </div>
        </div>
      );
    
    case 'select':
      // Check for readonly (both lowercase and camelCase for compatibility)
      const isSelectReadonly = field.readonly === true || field.readOnly === true;
      const isSelectDisabled = field.disabled === true;
      
      // Handle value conversion for lookupEndpoint fields
      // If value is an object (from API response), extract the ID
      let selectValue = value;
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Extract ID from object (handles {id: 1, name: "..."} format)
        selectValue = value.id || value.value || value[field.name] || '';
      } else if (value !== null && value !== undefined) {
        // Convert to string for comparison with option values
        selectValue = String(value);
      } else {
        selectValue = '';
      }

      // Locked lookup fields always show their locked value once resolved
      if (
        field.lockedLookupName &&
        field.defaultValue != null &&
        (selectValue === '' || selectValue == null)
      ) {
        selectValue = String(field.defaultValue);
      }
      
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <Select
                value={selectValue}
                onChange={(e) => {
                  // Convert to number if field expects integer (for ID fields)
                  const newValue = field.validation?.integer 
                    ? (e.target.value ? parseInt(e.target.value, 10) : null)
                    : e.target.value;
                  setFormData(prev => ({ ...prev, [field.name]: newValue }));
                }}
                required={isRequired}
                placeholder={`${labels.selectPrefix} ${field.label}`}
                disabled={isSelectDisabled}
                readOnly={isSelectReadonly}
                showClearButton={!isSelectDisabled && !isSelectReadonly && !field.showLockIcon}
              >
                {field.options?.map(option => {
                  const optionValue = typeof option === 'object' ? option.value : option;
                  const optionLabel = typeof option === 'object' ? option.label : option;
                  // Convert option value to string for comparison
                  const optionValueStr = String(optionValue);
                  return (
                    <SelectItem 
                      key={optionValueStr} 
                      value={optionValueStr}
                    >
                      {optionLabel}
                    </SelectItem>
                  );
                })}
              </Select>
            </div>
            {field.showLockIcon && (
              <span
                className="inline-flex items-center justify-center shrink-0 text-gray-500 dark:text-gray-400"
                title="Locked"
                aria-label="Locked"
              >
                <LockIcon className="w-4 h-4" />
              </span>
            )}
          </div>
          {hasError && (
            <div className="mt-1 text-sm text-red-600 dark:text-red-400">
              {formErrors[field.name]}
            </div>
          )}
        </div>
      );
    
    case 'multiSelect':
      // Handle array values - convert from JSON string or array
      let currentValue = [];
      if (Array.isArray(value)) {
        currentValue = value;
      } else if (typeof value === 'string' && value.trim() !== '') {
        try {
          const parsed = JSON.parse(value);
          currentValue = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.warn(`Failed to parse ${field.name}:`, e);
          currentValue = [];
        }
      } else if (value === null || value === undefined) {
        currentValue = [];
      }
      
      // Convert options to the format expected by MultiSelect component
      // MultiSelect now accepts {value, label} objects
      const multiSelectOptions = field.options?.map(option => {
        const optionValue = typeof option === 'object' ? option.value : option;
        const optionLabel = typeof option === 'object' ? option.label : String(option);
        return { value: optionValue, label: optionLabel };
      }) || [];
      
      const handleMultiSelectChange = (selectedValues) => {
        // Convert selected values back to numbers if they're numeric
        // Ensure we always convert to numbers for numeric fields (like IDs)
        const numericValues = selectedValues.map(val => {
          // Handle both string and number inputs
          if (val === null || val === undefined) return null;
          const numVal = typeof val === 'string' ? Number(val) : Number(val);
          return isNaN(numVal) ? null : numVal;
        }).filter(val => val !== null); // Remove any null/invalid values
        
        setFormData(prev => ({ 
          ...prev, 
          [field.name]: numericValues.length > 0 ? numericValues : [] 
        }));
      };
      
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <MultiSelect
            options={multiSelectOptions}
            value={currentValue}
            onChange={handleMultiSelectChange}
            placeholder={field.placeholder || `Select ${field.label.toLowerCase()}...`}
            className={fieldErrorClass ? `border-red-500 ${fieldErrorClass}` : ''}
            maxDisplay={3}
          />
          {currentValue.length > 0 && (
            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              {currentValue.length} {field.label.toLowerCase().replace('involved ', '')}{currentValue.length !== 1 ? 's' : ''} selected
            </div>
          )}
          {hasError && (
            <div className="mt-1 text-sm text-red-600 dark:text-red-400">
              {formErrors[field.name]}
            </div>
          )}
        </div>
      );
    
    case 'textarea':
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <Textarea
            value={value}
            onChange={async (e) => {
              const newValue = e.target.value;
              setFormData(prev => ({ ...prev, [field.name]: newValue }));
              
              // Real-time validation (simplified)
              const error = await validateField(field.name, newValue);
              setFormErrors(prev => ({ ...prev, [field.name]: error || '' }));
            }}
            placeholder={field.placeholder || `${labels.enterPrefix} ${field.label.toLowerCase()}`}
            required={isRequired}
            rows={field.rows || 4}
            className={fieldErrorClass}
          />
          {hasError && (
            <div className="mt-1 text-sm text-red-600 dark:text-red-400">
              {formErrors[field.name]}
            </div>
          )}
        </div>
      );

    case 'taskList': {
      const taskList = parseTaskListValue(value);

      const updateTaskList = async (nextTaskList) => {
        const nextValue = toTaskListString(nextTaskList);
        setFormData((prev) => ({ ...prev, [field.name]: nextValue }));
        const error = await validateField(field.name, nextValue);
        setFormErrors((prev) => ({ ...prev, [field.name]: error || '' }));
      };

      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <div
            className={cn(
              'space-y-3 rounded-xl border p-3 shadow-sm',
              theme.border.light,
              theme.colors.surface,
            )}
          >
            <div className="flex items-center justify-between">
              <p className={cn('text-xs font-semibold uppercase tracking-wide', theme.text.secondary)}>
                Checklist
              </p>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[11px] font-medium',
                  theme.colors.background,
                  theme.text.secondary,
                )}
              >
                {taskList.length} {taskList.length === 1 ? 'task' : 'tasks'}
              </span>
            </div>

            {taskList.length === 0 ? (
              <div
                className={cn(
                  'rounded-lg border border-dashed px-3 py-4 text-center text-xs',
                  theme.border.light,
                  theme.text.muted,
                )}
              >
                No tasks yet. Add your first task below.
              </div>
            ) : (
              taskList.map((item, index) => (
                <div
                  key={`${field.name}-${index}`}
                  className={cn(
                    'rounded-lg border p-2.5',
                    item.isDone
                      ? 'border-emerald-200 bg-emerald-50'
                      : cn(theme.border.light, theme.colors.surface),
                  )}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span className={cn('text-[11px] font-semibold', theme.text.muted)}>
                      Task #{index + 1}
                    </span>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[10px] font-medium',
                        item.isDone
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-900',
                      )}
                    >
                      {item.isDone ? 'Done' : 'Open'}
                    </span>
                  </div>

                  <div className="grid grid-cols-12 gap-2 items-center">
                    <Input
                      type="text"
                      value={item.task}
                      onChange={(e) => {
                        const next = [...taskList];
                        next[index] = { ...next[index], task: e.target.value };
                        updateTaskList(next);
                      }}
                      placeholder="Task description"
                      className={cn(
                        "col-span-10",
                        item.isDone ? 'line-through opacity-70' : ''
                      )}
                      disabled={Boolean(item.isDone)}
                      style={item.isDone ? { textDecoration: 'line-through' } : undefined}
                    />
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <Switch
                        checked={Boolean(item.isDone)}
                        onChange={(checked) => {
                          const next = [...taskList];
                          next[index] = { ...next[index], isDone: Boolean(checked) };
                          updateTaskList(next);
                        }}
                        size="sm"
                        variant="simple"
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          const shouldDelete = typeof confirmTaskDelete === 'function'
                            ? await confirmTaskDelete()
                            : window.confirm('Are you sure you want to delete this task?');
                          if (!shouldDelete) return;
                          const next = taskList.filter((_, i) => i !== index);
                          updateTaskList(next);
                        }}
                        className="h-7 w-7 rounded-md bg-red-50 text-red-600 hover:bg-red-100"
                        title="Remove task"
                      >
                        X
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}

            <button
              type="button"
              onClick={() => updateTaskList([...taskList, { task: '', isDone: false }])}
              className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-100"
            >
              + Add Task
            </button>
          </div>
          {hasError && (
            <div className="mt-1 text-sm text-red-600">
              {formErrors[field.name]}
            </div>
          )}
        </div>
      );
    }

    case 'radio':
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <RadioGroup
            options={field.options || []}
            value={value}
            onChange={(selectedValue) => setFormData(prev => ({ ...prev, [field.name]: selectedValue }))}
            name={field.name}
            required={isRequired}
            orientation={field.orientation || 'vertical'}
          />
        </div>
      );

    case 'switch':
    case 'boolean':
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <div className="mt-2">
            <Switch
              checked={Boolean(value)}
              onChange={(checked) => setFormData(prev => ({ ...prev, [field.name]: checked }))}
              label={field.switchLabel || field.label}
              description={field.description}
              name={field.name}
              size={field.size || 'default'}
              labelPosition={field.labelPosition || 'left'}
            />
          </div>
        </div>
      );

    case 'toggle':
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <Toggle
            pressed={Boolean(value)}
            onPressedChange={(pressed) => setFormData(prev => ({ ...prev, [field.name]: pressed }))}
            variant={field.variant || 'default'}
            size={field.size || 'default'}
          >
            {field.toggleLabel || field.label}
          </Toggle>
        </div>
      );

    case 'checkbox':
      return (
        <div key={field.name} className="mb-0">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.checked }))}
              required={isRequired}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {field.label}
            </span>
          </label>
        </div>
      );

    case 'color':
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <ColorInput
            value={value}
            onChange={async (newValue) => {
              setFormData(prev => ({ ...prev, [field.name]: newValue }));
              
              // Real-time validation
              const error = await validateField(field.name, newValue);
              setFormErrors(prev => ({ ...prev, [field.name]: error || '' }));
            }}
            onBlur={async (e) => {
              // Additional validation on blur if needed
              const blurValue = e?.target?.value !== undefined ? e.target.value : value;
              const error = await validateField(field.name, blurValue);
              setFormErrors(prev => ({ ...prev, [field.name]: error || '' }));
            }}
            placeholder={field.placeholder}
            required={isRequired}
            error={hasError ? formErrors[field.name] : null}
            disabled={field.readOnly}
          />
        </div>
      );
    
    case 'date':
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <DateInput
            value={value}
            onChange={async (e) => {
              const newValue = e.target.value;
              setFormData(prev => ({ ...prev, [field.name]: newValue }));
              
              // Real-time validation
              const error = await validateField(field.name, newValue);
              setFormErrors(prev => ({ ...prev, [field.name]: error || '' }));
            }}
            name={field.name}
            placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`}
            required={isRequired}
            readOnly={field.readOnly}
            disabled={field.disabled}
            minDate={field.minDate}
            maxDate={field.maxDate}
            showClearButton={field.showClearButton !== false}
            className={hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
          />
          {hasError && (
            <div className="mt-1 text-sm text-red-600 dark:text-red-400">
              {formErrors[field.name]}
            </div>
          )}
        </div>
      );

    case 'autocomplete':
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <Autocomplete
            endpoint={field.autocompleteEndpoint}
            options={field.options || []}
            value={value}
            onChange={async (selectedValue) => {
              setFormData(prev => ({ ...prev, [field.name]: selectedValue }));
              
              // Real-time validation
              const error = await validateField(field.name, selectedValue);
              setFormErrors(prev => ({ ...prev, [field.name]: error || '' }));
            }}
            placeholder={field.placeholder || `Type to search ${field.label.toLowerCase()}...`}
            disabled={field.disabled}
            required={isRequired}
            error={hasError ? formErrors[field.name] : null}
            maxLength={field.maxLength}
            className={hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
          />
          {hasError && (
            <div className="mt-1 text-sm text-red-600 dark:text-red-400">
              {formErrors[field.name]}
            </div>
          )}
        </div>
      );

    case 'text':
    case 'email':
    case 'password':
    case 'url':
    case 'tel':
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <TextInput
            type={field.type}
            value={value}
            onChange={async (e) => {
              const newValue = e.target.value;
              setFormData(prev => ({ ...prev, [field.name]: newValue }));
              
              // Real-time validation
              const error = await validateField(field.name, newValue);
              setFormErrors(prev => ({ ...prev, [field.name]: error || '' }));
            }}
            name={field.name}
            placeholder={field.placeholder || `${labels.enterPrefix} ${field.label.toLowerCase()}`}
            required={isRequired}
            readOnly={field.readOnly}
            disabled={field.disabled}
            maxLength={field.maxLength}
            minLength={field.minLength}
            pattern={field.pattern}
            autoComplete={field.autoComplete}
            showCharacterCount={field.showCharacterCount || (field.maxLength && field.maxLength > 0)}
            showClearButton={field.showClearButton !== false}
            size={field.size || 'default'}
            variant={field.variant || 'default'}
            prefix={field.prefix}
            suffix={field.suffix}
            helperText={field.helperText}
            error={hasError ? formErrors[field.name] : null}
            className={hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
          />
        </div>
      );

    case 'number':
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <NumberInput
            value={value}
            onChange={async (e) => {
              const newValue = e.target.value;
              setFormData(prev => ({ ...prev, [field.name]: newValue }));
              
              // Real-time validation
              const error = await validateField(field.name, newValue);
              setFormErrors(prev => ({ ...prev, [field.name]: error || '' }));
            }}
            name={field.name}
            placeholder={field.placeholder || `${labels.enterPrefix} ${field.label.toLowerCase()}`}
            required={isRequired}
            readOnly={field.readOnly}
            disabled={field.disabled}
            min={field.min}
            max={field.max}
            step={field.step || 1}
            decimals={field.decimals}
            showClearButton={field.showClearButton !== false}
            showSteppers={field.showSteppers !== false}
            allowNegative={field.allowNegative !== false}
            thousandsSeparator={field.thousandsSeparator !== false}
            currency={field.currency}
            percentage={field.percentage || false}
            size={field.size || 'default'}
            variant={field.variant || 'default'}
            prefix={field.prefix}
            suffix={field.suffix}
            helperText={field.helperText}
            error={hasError ? formErrors[field.name] : null}
            className={hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
          />
        </div>
      );
    
    case 'fileUpload':
      // Use FormFileUpload_2 if specified in field config or config-level setting
      const FileUploadComponent = field.useFormFileUpload2 || config.useFormFileUpload2 
        ? FormFileUpload_2 
        : FormFileUpload;
      
      return (
        <div key={field.name} className="mb-0">
          <FileUploadComponent
            name={field.name}
            value={Array.isArray(value) ? value : []}
            onChange={async (e) => {
              // For fileUpload, the value is already the file attachments array/object
              const newValue = e.target.value || e.target.fileAttachments || [];
              setFormData(prev => ({ ...prev, [field.name]: newValue }));
              
              // Skip validation for fileUpload fields
              setFormErrors(prev => ({ ...prev, [field.name]: '' }));
            }}
            label={field.label}
            required={isRequired}
            error={hasError ? formErrors[field.name] : null}
            multiple={field.multiple || false}
            accept={field.accept}
            maxSize={field.maxSize}
            maxFiles={field.maxFiles}
            placeholder={field.placeholder}
            showPreview={field.showPreview !== false}
            disabled={field.disabled}
            readOnly={field.readOnly}
            // Entity-specific props (will be passed from parent form)
            entityTypeId={config.entityTypeId}
            entityId={formData.id || null} // Use the current record ID (null for new records)
            userId={config.userId || null}
            description={field.description || ''}
            isPrivate={field.isPrivate || false}
            // Callbacks
            onFileUpload={field.onFileUpload}
            onFileRemove={field.onFileRemove}
            onFilesToDeleteChange={(filesToDelete) => {
              setFilesToDelete(filesToDelete);
            }}
            onError={(error) => {
              setFormErrors(prev => ({ ...prev, [field.name]: error }));
            }}
          />
        </div>
      );

    default:
      // Check for readonly (both lowercase and camelCase for compatibility)
      const isReadonly = field.readonly === true || field.readOnly === true;
      
      // Handle nested object values for input fields
      let inputValue = value;
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        inputValue = value.version || value.name || value.title || value.userDisplayName || '';
      }
      
      return (
        <div key={field.name} className="mb-0">
          <Label>
            {field.label}
            <RequiredIndicator isRequired={isRequired} config={config} />
          </Label>
          <Input
            type={field.type || 'text'}
            value={inputValue}
            onChange={async (e) => {
              const newValue = e.target.value;
              setFormData(prev => ({ ...prev, [field.name]: newValue }));
              
              // Real-time validation (simplified)
              const error = await validateField(field.name, newValue);
              setFormErrors(prev => ({ ...prev, [field.name]: error || '' }));
            }}
            placeholder={field.placeholder || `${labels.enterPrefix} ${field.label.toLowerCase()}`}
            required={isRequired}
            step={field.step}
            className={fieldErrorClass}
            disabled={isReadonly || field.disabled}
            readOnly={isReadonly}
          />
          {hasError && (
            <div className="mt-1 text-sm text-red-600 dark:text-red-400">
              {formErrors[field.name]}
            </div>
          )}
        </div>
      );
  }
};
